import {
  BoxGeometry,
  CapsuleGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  Scene,
  SphereGeometry,
} from 'three';
import { BLOCK_STEP, CITY, HALF_CITY, PALETTE, POPULATION } from '../config/settings';
import { Updatable } from '../core/types';
import { pick, randFloat, randInt } from '../utils/random';

type Axis = 'x' | 'z';

interface Pedestrian {
  readonly group: Group;
  readonly legL: Mesh;
  readonly legR: Mesh;
  readonly axis: Axis;
  readonly min: number;
  readonly max: number;
  readonly speed: number;
  direction: number;
  phase: number;
}

/**
 * Human-scale (~1.85 m) pedestrians that walk back and forth along block edges
 * with a simple swinging-leg gait. Feet sit at the group origin so they always
 * stand correctly on the sidewalk.
 */
export class PedestrianSystem implements Updatable {
  private readonly people: Pedestrian[] = [];

  constructor(scene: Scene) {
    for (let i = 0; i < POPULATION.PEDESTRIANS; i++) {
      const ped = this.spawn();
      scene.add(ped.group);
      this.people.push(ped);
    }
  }

  update(dt: number): void {
    for (const p of this.people) {
      const step = p.speed * p.direction * dt;
      const next = p.group.position[p.axis] + step;
      if (next > p.max) {
        p.group.position[p.axis] = p.max;
        p.direction = -1;
        this.faceAlong(p);
      } else if (next < p.min) {
        p.group.position[p.axis] = p.min;
        p.direction = 1;
        this.faceAlong(p);
      } else {
        p.group.position[p.axis] = next;
      }
      p.phase += dt * p.speed * 3;
      const swing = Math.sin(p.phase) * 0.5;
      p.legL.rotation.x = swing;
      p.legR.rotation.x = -swing;
    }
  }

  private spawn(): Pedestrian {
    const block = this.randomBlockCentre();
    const off = CITY.BLOCK_SIZE / 2 - 2;
    const edge = randInt(4);
    const axis: Axis = edge < 2 ? 'x' : 'z';

    let x: number;
    let z: number;
    if (edge === 0) {
      x = block.cx + randFloat(-off, off);
      z = block.cz - off;
    } else if (edge === 1) {
      x = block.cx + randFloat(-off, off);
      z = block.cz + off;
    } else if (edge === 2) {
      x = block.cx - off;
      z = block.cz + randFloat(-off, off);
    } else {
      x = block.cx + off;
      z = block.cz + randFloat(-off, off);
    }

    const { group, legL, legR } = this.createBody();
    group.scale.setScalar(randFloat(0.9, 1.12));
    group.position.set(x, CITY.CURB_HEIGHT, z);

    const centre = axis === 'x' ? block.cx : block.cz;
    const ped: Pedestrian = {
      group,
      legL,
      legR,
      axis,
      min: centre - off,
      max: centre + off,
      speed: randFloat(1.5, 3.2),
      direction: Math.random() < 0.5 ? 1 : -1,
      phase: randFloat(0, Math.PI * 2),
    };
    this.faceAlong(ped);
    return ped;
  }

  private randomBlockCentre(): { cx: number; cz: number } {
    const gx = randInt(CITY.GRID);
    const gz = randInt(CITY.GRID);
    return {
      cx: -HALF_CITY + CITY.ROAD_WIDTH / 2 + gx * BLOCK_STEP + CITY.BLOCK_SIZE / 2,
      cz: -HALF_CITY + CITY.ROAD_WIDTH / 2 + gz * BLOCK_STEP + CITY.BLOCK_SIZE / 2,
    };
  }

  private createBody(): { group: Group; legL: Mesh; legR: Mesh } {
    const group = new Group();
    const legGeo = new BoxGeometry(0.22, 0.85, 0.24);
    const legMat = new MeshLambertMaterial({ color: 0x2c3550 });
    const legL = new Mesh(legGeo, legMat);
    legL.position.set(-0.15, 0.42, 0);
    legL.castShadow = true;
    const legR = new Mesh(legGeo, legMat);
    legR.position.set(0.15, 0.42, 0);
    legR.castShadow = true;
    group.add(legL, legR);

    const torso = new Mesh(new CapsuleGeometry(0.28, 0.55, 4, 8), new MeshLambertMaterial({ color: pick(PALETTE.shirts) }));
    torso.position.y = 1.15;
    torso.castShadow = true;
    group.add(torso);

    const head = new Mesh(new SphereGeometry(0.21, 12, 12), new MeshLambertMaterial({ color: pick(PALETTE.skin) }));
    head.position.y = 1.62;
    head.castShadow = true;
    group.add(head);

    return { group, legL, legR };
  }

  private faceAlong(p: Pedestrian): void {
    if (p.axis === 'x') p.group.rotation.y = p.direction > 0 ? Math.PI / 2 : -Math.PI / 2;
    else p.group.rotation.y = p.direction > 0 ? 0 : Math.PI;
  }
}
